/**
 * ErrorState Component
 * 
 * Reusable error state component for API errors and other failures
 * Follows Mantine design system and provides recovery actions
 */

import React from 'react';
import { Box, Button, Center, Stack, Text, Alert } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import classes from './ErrorState.module.css';

export interface ErrorStateProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
  showIcon?: boolean;
  variant?: 'default' | 'alert' | 'minimal';
  centered?: boolean;
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  onRetry,
  retryLabel = 'Try again',
  showIcon = true,
  variant = 'default',
  centered = true,
}: ErrorStateProps) {
  const content = (
    <Stack align="center" gap="md" className={classes.errorContainer}>
      {showIcon && (
        <IconAlertTriangle size={48} className={classes.errorIcon} />
      )}
      
      <Stack align="center" gap="xs">
        <Text size="lg" fw={500} ta="center">
          {title}
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          {message}
        </Text>
      </Stack>
      
      {onRetry && (
        <Button
          leftSection={<IconRefresh size={16} />}
          onClick={onRetry}
          variant="outline"
        >
          {retryLabel}
        </Button>
      )}
    </Stack>
  );

  if (variant === 'alert') {
    return (
      <Alert
        icon={showIcon ? <IconAlertTriangle size={16} /> : undefined}
        title={title}
        color="red"
        variant="light"
      >
        <Stack gap="md">
          <Text size="sm">{message}</Text>
          {onRetry && (
            <Button
              leftSection={<IconRefresh size={16} />}
              onClick={onRetry}
              variant="outline"
              size="xs"
            >
              {retryLabel}
            </Button>
          )}
        </Stack>
      </Alert>
    );
  }

  if (variant === 'minimal') {
    return (
      <Box className={classes.minimalContainer}>
        <Text size="sm" c="red">
          {title}: {message}
        </Text>
        {onRetry && (
          <Button onClick={onRetry} variant="subtle" size="xs" ml="xs">
            {retryLabel}
          </Button>
        )}
      </Box>
    );
  }

  if (centered) {
    return (
      <Center className={classes.centeredContainer}>
        {content}
      </Center>
    );
  }

  return content;
}